import { IronMeta } from '@polymer/iron-meta/iron-meta.js';
import DOMPurify from 'dompurify';

/**
 * @typedef { 'IRON-ICON' | 'MATERIAL-SYMBOL' } IconType
 */

/**
 * @param {String} icon
 * @returns { ?IconType }
 */
function InferIconType(icon) {
  if (!icon) return null;

  // We always specify an iconset (the part before the colon) for iron icons.
  if (icon.includes(':') || icon.includes('-')) return 'IRON-ICON';

  return 'MATERIAL-SYMBOL';
}

const MATERIAL_SYMBOLS = {};
const MATERIAL_SYMBOL_PROMISES = {};
// Material symbols whose fetch failed.  These are tracked so a symbol we could not load is treated as
// missing (and gets the fallback icon) rather than as still loading, which would hide it indefinitely.
const MATERIAL_SYMBOL_FETCH_FAILURES = new Set();
const EXTERNAL_ICON_LOADED_CALLBACKS = [];
const CUSTOM_ICON_SET_NAME = 'model-icons';
let CUSTOM_ICON_SVGS = {};
// Custom icons load asynchronously, so this tracks whether they have arrived yet.
let hasRegisteredCustomIcons = false;

/**
This module is used to convert a geostyle object structure to an svg icon

geoStyle parameter format:
{
    c: (String) A color value to use for the icon
    ci: (String) The icon name for a connection's icon
    cis: (Number) The size for the connection icon
    cic: (String) A color value for the color of the icon
    f: Indicates if there should be a ring around the icon for feedback
    i: (String) Indicates the style set and name for the icon to use (separated by :)
    is: (Array) An array of icons that should be displayed (each item in the array should have the same geoStyle format)
    r: (Number) The angle to rotate the icon by
    ri: (String) A color value for a ring around the icon
    s: (Number) The size of the icon
    x: (String) Text to display with the icon
    xc: (String) A color value for the color of the text
}

options parameter format:
{
    haloColor: (String) A color value for the halo around the icon or a function to compute the haloColor from the item's data
    dragging: (Boolean) Indicates if we should generate a dragging icon
    anchorX: (Number) Override for the x anchor position to use
    anchorY: (Number) Override for the y anchor position to use
    shrinkDoneItems: (Boolean) Indicates if we should shrink the icon if it has an 'm' property
}
**/

function GeoStyleToIcon(geoStyle, options) {
  options = options || {};

  // Check if the icon should be a style for dragging
  if (options.dragging) {
    return GeoStyleToIcon(
      {
        i: 'katapult-map:rubberband',
        s: 48,
        c: 'rgba(0,0,0,0.5)'
      },
      {
        haloColor: options.haloColor,
        anchorX: 23,
        anchorY: 46,
        dragging: false
      }
    );
  }

  // Contains the SVGs for each individual icon
  let iconSVG = '';
  // Get the total size of the geoStyle item (this function will
  // return the largest size of the icons in the icon array, or just the size
  // of a single icon if there is no array)
  const initialSize = GetIconSize(geoStyle, options);
  const totalSize = structuredClone(initialSize);

  // Default SVG
  let url = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="${totalSize.width}" height="${totalSize.height}">`;

  let haloColor = geoStyle.haloColor ?? (typeof options.haloColor == 'function' ? options.haloColor(geoStyle) : options.haloColor);
  // Check if we have an option to add a halo/ring
  if (haloColor || geoStyle.f || geoStyle.ri || geoStyle.doughnut) {
    // Adjust the size for the ring/halo
    if (totalSize.width > 20) totalSize.width = totalSize.height = totalSize.width * 1.5;
    else totalSize.width = totalSize.height = 36;
    // Define the amount the halo will spread
    let blurSpread = 3;
    // Build the SVG data for the ring/halo
    url = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="${totalSize.width}" height="${totalSize.height}">`;
    if (haloColor)
      url += `<defs><filter id="blur"><feGaussianBlur in="SourceGraphic" stdDeviation="${blurSpread}" /></filter></defs><circle r="${
        (totalSize.width - blurSpread * 2) / 2
      }" cx="${totalSize.width / 2}" cy="${totalSize.height / 2}" fill="${haloColor}" filter="url(#blur)" />`;
    if (geoStyle.f)
      url += `<circle r="${totalSize.width / 2.95}" cx="${totalSize.width / 2}" cy="${totalSize.height / 2}" fill="rgba(255,0,0,0.8)" />`;
    if (geoStyle.ri)
      url += `<circle r="${totalSize.width / 2.95}" cx="${totalSize.width / 2}" cy="${totalSize.height / 2}" fill-opacity="0" stroke-width="5" stroke="${geoStyle.ri}" />`;
    if (geoStyle.doughnut) {
      const { thickness = 3, rotation = 0, segments = [] } = geoStyle.doughnut;
      let segmentOffset = 0.25 - rotation / 100; // Offset is CCW.
      const radius = (initialSize.width + thickness) / 2;
      const circumference = 2 * Math.PI * radius;
      const fracToDist = (ratio) => ratio * circumference;
      segments.forEach(({ percent = 100, color = 'black' }) => {
        const segmentFraction = percent / 100;
        url += `<circle r="${radius}" cx="${totalSize.width / 2}" cy="${totalSize.height / 2}" fill="transparent" stroke="${color}" stroke-width="${thickness}" stroke-dasharray="${fracToDist(segmentFraction)} ${fracToDist(1 - segmentFraction)}" stroke-dashoffset="${fracToDist(segmentOffset)}"></circle>`;
        segmentOffset -= segmentFraction;
      });
    }
  }

  url += `<g transform="rotate(${geoStyle.r || 0}, ${totalSize.width / 2}, ${totalSize.height / 2})">`;

  // Get the list of icons to iterate over (make sure icon stack has items if it exists)
  const iconStack = Array.isArray(geoStyle.is) && geoStyle.is.length > 0 ? geoStyle.is : null;
  const iconList = iconStack ?? [geoStyle];
  // For stacked icons, skip rendering a layer whose icon source is still loading rather than
  // flashing the fallback icon under it (the marker re-renders once the source loads).
  const subIconOptions = iconStack ? { ...options, skipFallbackWhilePending: true } : options;
  iconList.forEach((subIconData) => {
    // Only include the item if it shouldn't be hidden
    if (!options.hiddenItems || !options.hiddenItems[subIconData.u]) {
      // Get the icon for the single icon
      let iconData = GetIconSVG(subIconData, subIconOptions);
      if (iconData) {
        // Position the icon in the center of the total icon space
        let centerX = (totalSize.width - iconData.size.width) / 2;
        let centerY = (totalSize.height - iconData.size.height) / 2;
        iconSVG += `<g transform="translate(${centerX} ${centerY})">${iconData.svg}</g>`;
      }
    }
  });
  geoStyle.xc = geoStyle.xc || 'white';
  // Check if we should add text as well to the icon
  if (geoStyle.x != null) {
    iconSVG += `<text x="${totalSize.width / 2}" y="${totalSize.height / 2 + 3}" style="${
      geoStyle.xc ? 'fill:' + geoStyle.xc : ''
    }" font-family="Verdana" font-size="${initialSize.width / 2}" font-weight="bold" text-anchor="middle">${geoStyle.x}</text>`;
  }

  // If there is nothing in iconSVG, then none of the
  // icons should render and we should return null
  if (!iconSVG) return null;

  // Add the SVGs for the icons
  url += iconSVG;
  // Finish the SVG tag
  url += '</g></svg>';
  // Escape # from color values
  url = url.replace(/\#/g, '%23');

  return {
    size: totalSize,
    anchor: { x: options.anchorX || totalSize.width / 2, y: options.anchorY || totalSize.height / 2 },
    url
  };
}

function GetIconSize(geoStyle, options) {
  options = options || {};
  let maxSize = 0;
  let iconList = Array.isArray(geoStyle.is) && geoStyle.is.length > 0 ? geoStyle.is : [geoStyle];
  iconList.forEach((subIconData) => {
    // Get a default icon size
    let iconSize = subIconData.s || 10;
    // Check if this is a connection icon
    if (subIconData.ci) {
      iconSize = subIconData.cis || 1.5;
    }
    // If we should shrink done items, then check if the item has m set
    if (options.shrinkDoneItems && subIconData.m) {
      iconSize *= 0.65;
    }
    // Check if we should apply a multiplier
    if (options.sizeMultiplier) {
      iconSize *= options.sizeMultiplier;
    }
    // Get the larger size
    maxSize = Math.max(maxSize, iconSize);
  });
  // Don't let the size go below 10
  if (maxSize < 10) maxSize = 10;
  // Maybe some day we'll need different width and height, but right now they are the same
  return { width: maxSize, height: maxSize };
}

function GetIconSVG(geoStyle, options = {}) {
  const DEFAULT_ICON = ['katapult-map', 'circle'];

  // Initialize some default geostyle properties.
  geoStyle.r ??= 0;
  geoStyle.xc ??= 'white';

  const { i: nodeIconString, ci: connectionIconString, c: _iconColor, cic: connectionIconColor } = geoStyle;

  const iconColor = connectionIconColor ?? _iconColor;
  const iconSize = GetIconSize(geoStyle, options);

  const iconString = nodeIconString ?? connectionIconString;

  let iconSVGContent = null;
  let ironIconSet = null;
  const iconType = InferIconType(iconString);
  switch (iconType) {
    case 'IRON-ICON': {
      // Iron icons usually have the iconset followed by a colon and then the icon name.
      // Sometimes the iconset is omitted.  The iron-icon element uses the default icon set in this case ('icons').
      // It appears that the maps style editor always specifies the 'icons' icon set when used and the
      // existing functionality here was to default it to 'katapult-map' so I have preserved that behavior.
      const [iconName, iconSet = 'katapult-map'] = iconString?.split?.(':').reverse();
      ironIconSet = iconSet;

      // Resolve custom model icons from the registry and every other icon from an iconset.
      const isCustomModelIcon = iconSet == CUSTOM_ICON_SET_NAME;
      if (isCustomModelIcon) iconSVGContent = CUSTOM_ICON_SVGS[iconName] ?? null;
      else iconSVGContent = GetIronIconSVGContent(iconSet, iconName);
      break;
    }
    case 'MATERIAL-SYMBOL': {
      iconSVGContent = GetMaterialSymbolSVG(iconString);
      break;
    }
  }

  if (!iconSVGContent) {
    // Wait for a custom model icon that hasn't finished loading yet instead of showing the fallback icon.
    if (ironIconSet == CUSTOM_ICON_SET_NAME && !hasRegisteredCustomIcons) return null;

    // Let an opted-in layer skip the fallback icon while its icon source is still pending.
    if (options.skipFallbackWhilePending) {
      const ironIconsetPending = iconType == 'IRON-ICON' && !IsIronIconsetLoaded(ironIconSet);
      const materialSymbolPending = iconType == 'MATERIAL-SYMBOL' && !MATERIAL_SYMBOL_FETCH_FAILURES.has(iconString);
      if (ironIconsetPending || materialSymbolPending) return null;
    }

    console.warn('Failed to find SVG for icon:', iconString, iconSize, iconColor);
    iconSVGContent = GetIronIconSVGContent(...DEFAULT_ICON);
  }

  const svgStyle = iconColor ? `fill:${iconColor}; color:${iconColor}` : '';
  const unsanitizedSVG = `
  <svg width="${iconSize.width}" height="${iconSize.height}" viewBox="0 0 24 24" style="${svgStyle}">
  ${iconSVGContent}
  </svg>
  `;

  const sanitizedSVG = DOMPurify.sanitize(unsanitizedSVG);

  return {
    size: iconSize,
    svg: sanitizedSVG
  };
}

function GetMaterialSymbolSVG(iconName) {
  const materialSymbol = MATERIAL_SYMBOLS[iconName];
  if (!materialSymbol) {
    if (!MATERIAL_SYMBOL_FETCH_FAILURES.has(iconName)) FetchMaterialSymbolSVGContent(iconName);
    return null;
  }
  return materialSymbol;
}

/**
 * Checks whether an iron iconset has finished loading, telling a pending iconset apart from a missing icon.
 * @param {string} iconSet
 * @returns {boolean}
 */
function IsIronIconsetLoaded(iconSet) {
  if (!iconSet) return false;
  if (iconSet == CUSTOM_ICON_SET_NAME) return hasRegisteredCustomIcons;
  const iconSetMeta = new IronMeta({ type: 'iconset' });
  return !!iconSetMeta.byKey(iconSet);
}

function GetIronIconSVGContent(iconSet, iconName) {
  const iconSetMeta = new IronMeta({ type: 'iconset' });
  const iconSetRef = iconSetMeta.byKey(iconSet);
  if (!iconSetRef) return null;
  const textElem = document.createElement('text');
  const iconContent = iconSetRef.applyIcon(textElem, iconName);
  const contentIsValid = iconContent?.localName == 'svg' && iconContent.innerHTML != null;
  if (!contentIsValid) return null;
  return iconContent.innerHTML;
}

/**
 * Fetches the SVG for a material icon
 * @param {string} icon The name of the material icon
 */
async function FetchMaterialSymbolSVGContent(icon) {
  const url = `https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsrounded/${icon}/fill1/24px.svg`;
  const promise = (MATERIAL_SYMBOL_PROMISES[icon] ??= fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.text();
    })
    .then((svgString) => {
      MATERIAL_SYMBOLS[icon] = svgString;
      NotifyExternalIconLoaded();
      return svgString;
    })
    .catch((error) => {
      console.warn('Failed to fetch material symbol:', icon, error);
      // Flag the symbol as failed rather than leaving it pending forever, then notify listeners so
      // anything that skipped rendering while the fetch was in flight re-renders with the fallback
      // icon.  The settled promise stays cached so we don't refetch (and renotify) on every render.
      MATERIAL_SYMBOL_FETCH_FAILURES.add(icon);
      NotifyExternalIconLoaded();
      return null;
    }));
  return await promise;
}

/**
 * Notifies registered listeners that an external icon source finished loading.
 */
function NotifyExternalIconLoaded() {
  for (const callback of EXTERNAL_ICON_LOADED_CALLBACKS) callback();
}

/**
 * Replace the registered custom model icons and redraw anything already using them.
 * @param {Object<string, {svg: string}>|null} icons Custom icons keyed by the name map styles reference them by.
 */
function RegisterCustomIcons(icons) {
  // Register the SVG source of every icon, keyed by icon name.
  CUSTOM_ICON_SVGS = {};
  for (const [iconName, icon] of Object.entries(icons ?? {})) {
    if (!icon?.svg) continue;
    CUSTOM_ICON_SVGS[iconName] = icon.svg;
  }

  // Notify listeners that the available icons changed.
  hasRegisteredCustomIcons = true;
  NotifyExternalIconLoaded();
}

/**
 * Mark the custom icon set as not yet loaded, so icons wait for the next model's own set before drawing.
 */
function ResetCustomIconsLoaded() {
  hasRegisteredCustomIcons = false;
}

/**
 * Mark the custom icon set as loaded and notify listeners to redraw with the real icons.
 */
function MarkCustomIconsLoaded() {
  hasRegisteredCustomIcons = true;
  NotifyExternalIconLoaded();
}

function ListenForExternalIconLoaded(callback) {
  EXTERNAL_ICON_LOADED_CALLBACKS.push(callback);
  const disconnect = () => {
    const index = EXTERNAL_ICON_LOADED_CALLBACKS.indexOf(callback);
    if (index >= 0) EXTERNAL_ICON_LOADED_CALLBACKS.splice(index, 1);
  };
  return disconnect;
}

async function LoadExternalIcons(jobStyles) {
  const promises = [];
  for (const styleType in jobStyles) {
    if (styleType.startsWith('_')) continue;
    for (const styleRule of jobStyles[styleType]) {
      const icon = styleRule.icon ?? styleRule.connIcon;
      if (!icon) continue;
      const iconType = InferIconType(icon);
      if (iconType == 'MATERIAL-SYMBOL') {
        promises.push(GetMaterialSymbolSVG(icon));
      }
    }
  }
  await Promise.all(promises);
}

/**
 * Generates an SVG string for a halo/ring effect around an icon.
 * @param {string} color
 * @returns {string}
 */
const GOOGLE_MATERIAL_YELLOW_600 = '#FDD835';
function GetHaloSVG(color = GOOGLE_MATERIAL_YELLOW_600) {
  const svgString = String.raw`
    <svg width="100%" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50%" cy="50%" r="10" fill="${color}" style="filter: blur(2px)" />
    </svg>
    `;
  const sanitizedSVG = DOMPurify.sanitize(svgString);
  return sanitizedSVG;
}

export {
  GeoStyleToIcon,
  GetIconSize,
  GetIconSVG,
  LoadExternalIcons,
  ListenForExternalIconLoaded,
  RegisterCustomIcons,
  ResetCustomIconsLoaded,
  MarkCustomIconsLoaded,
  CUSTOM_ICON_SET_NAME,
  GetHaloSVG
};
